import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export default function NewCampaign() {
  const [params] = useSearchParams();
  const initialTest = params.get("test") || "rapido";
  const navigate = useNavigate();

  const [tests, setTests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    test_id: initialTest,
    company_id: "",
    titulo: "",
    setor_ghe: "",
    funcao: "",
    n_trabalhadores: 0,
    coletar_setor_funcao: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/psy/tests"), api.get("/companies")])
      .then(([t, c]) => {
        setTests(t.data.tests || []);
        setCompanies(c.data || []);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar"));
  }, []);

  const selectedTest = tests.find((t) => t.id === form.test_id);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_id) return toast.error("Selecione uma empresa");
    if (!form.titulo.trim()) return toast.error("Informe um título");
    setSubmitting(true);
    try {
      const r = await api.post("/psy/campaigns", {
        ...form,
        n_trabalhadores: Number(form.n_trabalhadores) || 0,
      });
      toast.success("Campanha criada com sucesso!");
      navigate(`/app/testes-psicossociais/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="psy-new-campaign">
      <header>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 mb-3"
        >
          <i className="fa-solid fa-arrow-left mr-1"></i> Voltar
        </button>
        <h1 className="text-2xl font-bold text-slate-900">Nova campanha de teste psicossocial</h1>
        <p className="text-slate-500 text-sm mt-1">
          Preencha os dados para gerar o link público de aplicação para os colaboradores.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-5 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Tipo de teste
          </label>
          <select
            value={form.test_id}
            onChange={(e) => setForm({ ...form, test_id: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
            data-testid="select-test-id"
          >
            {tests.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.question_count} questões · {t.time_estimate})
              </option>
            ))}
          </select>
          {selectedTest && (
            <p className="text-xs text-slate-500 mt-1.5"><i className="fa-solid fa-circle-info mr-1 text-emerald-600"></i>{selectedTest.description}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Empresa avaliada
          </label>
          <select
            value={form.company_id}
            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
            data-testid="select-company"
            required
          >
            <option value="">Selecione…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome_fantasia || c.razao_social} {c.cnpj ? `· ${c.cnpj}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Título da campanha
          </label>
          <input
            type="text"
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
            placeholder="Ex.: Avaliação 2026 — Indústria São Paulo"
            data-testid="input-titulo"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Setor / GHE / Grupo (opcional)
            </label>
            <input
              type="text"
              value={form.setor_ghe}
              onChange={(e) => setForm({ ...form, setor_ghe: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
              placeholder="Ex.: Operação · Manutenção"
              data-testid="input-setor-ghe"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Função / Cargo (opcional)
            </label>
            <input
              type="text"
              value={form.funcao}
              onChange={(e) => setForm({ ...form, funcao: e.target.value })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
              placeholder="Ex.: Operadores de máquinas"
              data-testid="input-funcao"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Nº de trabalhadores alvo (para % de participação)
          </label>
          <input
            type="number"
            min="0"
            value={form.n_trabalhadores}
            onChange={(e) => setForm({ ...form, n_trabalhadores: e.target.value })}
            className="w-full md:w-48 rounded-lg border border-slate-300 px-3 py-2.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
            data-testid="input-ntrab"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer bg-slate-50 rounded-lg p-3 border border-slate-200">
          <input
            type="checkbox"
            checked={form.coletar_setor_funcao}
            onChange={(e) => setForm({ ...form, coletar_setor_funcao: e.target.checked })}
            className="mt-0.5 h-4 w-4"
            data-testid="check-coletar-setor"
          />
          <span className="text-sm text-slate-700">
            <b>Coletar setor/função do respondente</b> (mantém o anonimato individual mas permite recortes por GHE no dashboard).
          </span>
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-700 text-white font-semibold py-3 hover:shadow-lg transition-shadow disabled:opacity-60"
            data-testid="btn-submit-campaign"
          >
            {submitting ? <><i className="fa-solid fa-spinner fa-spin"></i>Criando…</> : <><i className="fa-solid fa-rocket"></i>Criar e gerar link público</>}
          </button>
        </div>
      </form>
    </div>
  );
}
